import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './Plans.css';
import PageTitle from '../shared/PageTitle';

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPlanModal, setShowNewPlanModal] = useState(false);
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    billingCycle: 'monthly',
    features: [],
    gst_rate: 18, // Default GST rate for India
    most_popular: false // Default most popular flag
  });
  const [featuresInput, setFeaturesInput] = useState('');
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to calculate GST and total price
  const calculatePricing = (basePrice, gstRate = 18) => {
    const base = parseFloat(basePrice) || 0;
    const gst = (base * gstRate) / 100;
    const total = base + gst;
    return {
      basePrice: base,
      gstAmount: gst,
      gstRate: gstRate,
      totalPrice: total
    };
  };

  // Helper function to parse features
  const parseFeatures = (featureData) => {
    if (!featureData) return [];

    try {
      if (typeof featureData === 'string') {
        // Try to parse as JSON string
        const parsed = JSON.parse(featureData);

        // Check if we have a features array in the parsed object
        if (parsed.features && Array.isArray(parsed.features)) {
          return parsed.features;
        }

        // Return the parsed array if it is one
        if (Array.isArray(parsed)) {
          return parsed;
        }

        // Return features as a string array if neither of the above
        return [featureData];
      }

      // If already an array, return it
      if (Array.isArray(featureData)) {
        return featureData;
      }

      // If it's an object with features property that's an array
      if (featureData.features && Array.isArray(featureData.features)) {
        return featureData.features;
      }

      return [];
    } catch (error) {
      console.error('Error parsing features:', error);
      return [];
    }
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      // Use relative path in production, localhost in development
      const apiBaseURL = process.env.REACT_APP_API_URL ||
        (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');

      console.log('Fetching subscription plans...');

      const response = await axios.get('/api/saas/subscriptions/plans', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(apiBaseURL && { baseURL: apiBaseURL })
      });

      console.log('Plans data received:', response.data);

      // Ensure response.data is an array
      const plansData = Array.isArray(response.data) ? response.data : [];

      // Properly parse features for each plan
      const plansWithParsedFeatures = plansData.map(plan => ({
        ...plan,
        features: parseFeatures(plan.features),
        gst_rate: plan.gst_rate || 18 // Default GST rate
      }));

      console.log('Plans with parsed features:', plansWithParsedFeatures);

      setPlans(plansWithParsedFeatures);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        toast.error(`Failed to load plans: ${error.response.data.message || error.message}`);
      } else {
        toast.error(`Failed to load plans: ${error.message}`);
      }
      setPlans([]);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'price' || name === 'gst_rate') {
      setFormData({
        ...formData,
        [name]: parseFloat(value) || ''
      });
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleFeaturesInputChange = (e) => {
    setFeaturesInput(e.target.value);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Plan name is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    if (formData.price === '') errors.price = 'Price is required';
    if (formData.price < 0) errors.price = 'Price cannot be negative';
    if (formData.gst_rate < 0 || formData.gst_rate > 100) errors.gst_rate = 'GST rate must be between 0 and 100';
    if (formData.features.length === 0 && !featuresInput.trim()) errors.features = 'At least one feature is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const prepareFormData = () => {
    // Convert features from input field to array if needed
    let features = [];
    if (featuresInput.trim()) {
      features = featuresInput.split('\n').filter(feature => feature.trim());
    } else if (Array.isArray(formData.features)) {
      features = formData.features;
    }

    return {
      name: formData.name,
      description: formData.description,
      price: parseFloat(formData.price) || 0,
      billing_cycle: formData.billingCycle,
      features: features,
      gst_rate: parseFloat(formData.gst_rate) || 18,
      most_popular: formData.most_popular
    };
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();

    const data = prepareFormData();

    if (!validateForm()) return;

    try {
      // Use relative path in production, localhost in development
      const apiBaseURL = process.env.REACT_APP_API_URL ||
        (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');

      await axios.post('/api/saas/subscriptions/plans', data, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(apiBaseURL && { baseURL: apiBaseURL })
      });

      toast.success('Subscription plan created successfully');
      setShowNewPlanModal(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      console.error('Error creating subscription plan:', error);
      toast.error(error.response?.data?.message || 'Failed to create subscription plan');
    }
  };

  const handleUpdatePlan = async (e) => {
    e.preventDefault();

    const data = prepareFormData();

    if (!validateForm()) return;

    try {
      // Use relative path in production, localhost in development
      const apiBaseURL = process.env.REACT_APP_API_URL ||
        (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');

      await axios.put(`/api/saas/subscriptions/plans/${currentPlan.id}`, data, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(apiBaseURL && { baseURL: apiBaseURL })
      });

      toast.success('Subscription plan updated successfully');
      setShowEditPlanModal(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      toast.error(error.response?.data?.message || 'Failed to update subscription plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this subscription plan? This action cannot be undone.')) {
      return;
    }

    try {
      // Use relative path in production, localhost in development
      const apiBaseURL = process.env.REACT_APP_API_URL ||
        (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');

      await axios.delete(`/api/saas/subscriptions/plans/${planId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(apiBaseURL && { baseURL: apiBaseURL })
      });

      toast.success('Subscription plan deleted successfully');
      fetchPlans();
    } catch (error) {
      console.error('Error deleting subscription plan:', error);
      toast.error(error.response?.data?.message || 'Failed to delete subscription plan');
    }
  };

  const openEditModal = (plan) => {
    setCurrentPlan(plan);

    // Parse features for display in the textarea
    const features = parseFeatures(plan.features);

    setFormData({
      name: plan.name,
      description: plan.description,
      price: plan.price,
      billingCycle: plan.billing_cycle || 'monthly',
      features: features,
      gst_rate: plan.gst_rate || 18,
      most_popular: plan.most_popular || false
    });

    // Convert features array to string for textarea
    setFeaturesInput(features.join('\n'));

    setShowEditPlanModal(true);
    setFormErrors({});
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      billingCycle: 'monthly',
      features: [],
      gst_rate: 18,
      most_popular: false
    });
    setFeaturesInput('');
    setFormErrors({});
    setCurrentPlan(null);
  };

  return (
    <div className="container-fluid mt-4">
      <PageTitle
        icon="fa-tags"
        title="Subscription Plans"
        action={
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setCurrentPlan(null);
              setShowNewPlanModal(true);
            }}
          >
            <i className="fas fa-plus-circle me-2"></i>
            Add New Plan
          </button>
        }
      />

      {/* Plans Cards */}
      <div className="row">
        {loading ? (
          <div className="col-12 text-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : plans.length === 0 ? (
          <div className="col-12 text-center my-5">
            <p>No subscription plans found. Click &ldquo;Add New Plan&ldquo; to create one.</p>
          </div>
        ) : (
          plans.map(plan => {
            const pricing = calculatePricing(plan.price, plan.gst_rate);
            return (
              <div key={plan.id} className="col-xl-6 col-lg-6 col-md-12 mb-4">
                <div className="card shadow h-100 plan-card">
                  <div className={`py-3 superadmin-plan-header ${plan.name === 'Pro' ? 'superadmin-pro-header' : 'superadmin-free-header'}`}>
                    <h6 className="m-0 font-weight-bold">{plan.name}</h6>
                    {plan.most_popular == true && (
                      <span className="recommended-badge">
                        Most Popular
                      </span>
                    )}
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="text-center mb-3">
                          <div className="plan-price">₹{pricing.basePrice.toFixed(2)}</div>
                          <div className="plan-price-period">per {plan.billing_cycle || 'month'}</div>
                          {pricing.gstAmount > 0 && (
                            <small className="text-muted d-block mt-1">
                              + ₹{pricing.gstAmount.toFixed(2)} GST ({plan.gst_rate}%)
                            </small>
                          )}
                        </div>
                        <p className="plan-description">{plan.description}</p>

                        {/* Pricing Breakdown */}
                        <div className="pricing-breakdown mb-2">
                          <h6>Pricing Details</h6>
                          <div className="pricing-row">
                            <span className="pricing-label">Base Price:</span>
                            <span className="pricing-value">₹{pricing.basePrice.toFixed(2)}</span>
                          </div>
                          {pricing.gstAmount > 0 && (
                            <div className="pricing-row">
                              <span className="pricing-label">GST ({plan.gst_rate}%):</span>
                              <span className="pricing-value">₹{pricing.gstAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="pricing-row">
                            <span className="pricing-label">Total Amount:</span>
                            <span className="pricing-value">₹{pricing.totalPrice.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="plan-features">
                          <h6 className="font-weight-bold">Features:</h6>
                          <ul className="list-unstyled">
                            {(plan.features || []).map((feature, index) => (
                              <li key={index} className="mb-2">
                                <i className="fas fa-check text-success mr-2"></i> {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer plan-card-footer">
                    <div className="d-flex justify-content-between">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openEditModal(plan)}
                      >
                        <i className="fas fa-edit mr-1"></i> Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeletePlan(plan.id)}
                        disabled={plan.name === 'Free'} // Prevent deleting the free plan
                      >
                        <i className="fas fa-trash mr-1"></i> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* New Plan Modal */}
      {showNewPlanModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header superadmin-modal-header">
                <h5 className="modal-title">Add New Subscription Plan</h5>
                <button type="button" className="btn-close text-white" onClick={() => setShowNewPlanModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleCreatePlan}>
                  <div className="mb-3">
                    <label className="form-label">Plan Name</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Basic, Pro, Enterprise"
                    />
                    {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Brief description of the plan"
                    />
                    {formErrors.description && <div className="invalid-feedback">{formErrors.description}</div>}
                  </div>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Base Price (INR)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={`form-control ${formErrors.price ? 'is-invalid' : ''}`}
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        placeholder="0.00"
                      />
                      {formErrors.price && <div className="invalid-feedback">{formErrors.price}</div>}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">GST Rate (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        className={`form-control ${formErrors.gst_rate ? 'is-invalid' : ''}`}
                        name="gst_rate"
                        value={formData.gst_rate}
                        onChange={handleInputChange}
                        placeholder="18"
                      />
                      {formErrors.gst_rate && <div className="invalid-feedback">{formErrors.gst_rate}</div>}
                      <small className="text-muted">Standard GST rate in India is 18%</small>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Billing Cycle</label>
                      <select
                        className="form-control"
                        name="billingCycle"
                        value={formData.billingCycle}
                        onChange={handleInputChange}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="annual">Annual</option>
                        <option value="quarterly">Quarterly</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="most_popular"
                      name="most_popular"
                      checked={formData.most_popular}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="most_popular">
                      Mark as Most Popular (highlighted to users)
                    </label>
                  </div>

                  {/* Pricing Preview */}
                  {formData.price && (
                    <div className="pricing-breakdown mb-3">
                      <h6>Pricing Preview</h6>
                      {(() => {
                        const preview = calculatePricing(formData.price, formData.gst_rate);
                        return (
                          <>
                            <div className="pricing-row">
                              <span className="pricing-label">Base Price:</span>
                              <span className="pricing-value">₹{preview.basePrice.toFixed(2)}</span>
                            </div>
                            {preview.gstAmount > 0 && (
                              <div className="pricing-row">
                                <span className="pricing-label">GST ({formData.gst_rate}%):</span>
                                <span className="pricing-value">₹{preview.gstAmount.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="pricing-row">
                              <span className="pricing-label">Total Amount:</span>
                              <span className="pricing-value">₹{preview.totalPrice.toFixed(2)}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Features (one per line)</label>
                    <textarea
                      className={`form-control ${formErrors.features ? 'is-invalid' : ''}`}
                      name="features"
                      value={featuresInput}
                      onChange={handleFeaturesInputChange}
                      rows="5"
                      placeholder="e.g.&#10;Up to 100 users&#10;10GB storage&#10;24/7 support&#10;Advanced analytics"
                    ></textarea>
                    {formErrors.features && <div className="invalid-feedback">{formErrors.features}</div>}
                    <small className="text-muted">Enter each feature on a new line</small>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowNewPlanModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Create Plan</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Plan Modal */}
      {showEditPlanModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header superadmin-modal-header">
                <h5 className="modal-title">Edit Subscription Plan</h5>
                <button type="button" className="btn-close text-white" onClick={() => setShowEditPlanModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleUpdatePlan}>
                  <div className="mb-3">
                    <label className="form-label">Plan Name</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                    {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                    />
                    {formErrors.description && <div className="invalid-feedback">{formErrors.description}</div>}
                  </div>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Base Price (INR)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={`form-control ${formErrors.price ? 'is-invalid' : ''}`}
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                      />
                      {formErrors.price && <div className="invalid-feedback">{formErrors.price}</div>}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">GST Rate (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        className={`form-control ${formErrors.gst_rate ? 'is-invalid' : ''}`}
                        name="gst_rate"
                        value={formData.gst_rate}
                        onChange={handleInputChange}
                      />
                      {formErrors.gst_rate && <div className="invalid-feedback">{formErrors.gst_rate}</div>}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Billing Cycle</label>
                      <select
                        className="form-control"
                        name="billingCycle"
                        value={formData.billingCycle}
                        onChange={handleInputChange}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="annual">Annual</option>
                        <option value="quarterly">Quarterly</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="edit_most_popular"
                      name="most_popular"
                      checked={formData.most_popular}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="edit_most_popular">
                      Mark as Most Popular (highlighted to users)
                    </label>
                  </div>

                  {/* Pricing Preview */}
                  {formData.price && (
                    <div className="pricing-breakdown mb-3">
                      <h6>Pricing Preview</h6>
                      {(() => {
                        const preview = calculatePricing(formData.price, formData.gst_rate);
                        return (
                          <>
                            <div className="pricing-row">
                              <span className="pricing-label">Base Price:</span>
                              <span className="pricing-value">₹{preview.basePrice.toFixed(2)}</span>
                            </div>
                            {preview.gstAmount > 0 && (
                              <div className="pricing-row">
                                <span className="pricing-label">GST ({formData.gst_rate}%):</span>
                                <span className="pricing-value">₹{preview.gstAmount.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="pricing-row">
                              <span className="pricing-label">Total Amount:</span>
                              <span className="pricing-value">₹{preview.totalPrice.toFixed(2)}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Features (one per line)</label>
                    <textarea
                      className={`form-control ${formErrors.features ? 'is-invalid' : ''}`}
                      name="features"
                      value={featuresInput}
                      onChange={handleFeaturesInputChange}
                      rows="5"
                      placeholder="e.g.&#10;Up to 100 users&#10;10GB storage&#10;24/7 support"
                    ></textarea>
                    {formErrors.features && <div className="invalid-feedback">{formErrors.features}</div>}
                    <small className="text-muted">Enter each feature on a new line</small>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowEditPlanModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Update Plan</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Plans; 